// QuillEditor.tsx
import React, { useEffect, useRef } from 'react';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { setTextEditorInstance } from '../../../../../redux/slices/textBlocks';

import {
  QUILL_API_ACTION_ADD_EMOJI,
  getUrlFromText,
  getUrlPreview,
  highlightQuillInput,
  setCursorPositionForQuill,
  getTextFromQuill
} from '../../creatorTextEditor/config';
import { TIMEOUT } from '../../contants';
import { useTranslate } from '@tolgee/react';
import { formats } from '../../creatorTextEditor/creatorQuillEditor';

const MENTION_TIMEOUT = 300;

const QuillEditor = ({
  value,
  onChange,
  mentionConfigUrls = [],
  placeholder = '',
  editor,
  setEditor,
  quillText
}) => {
  const { t } = useTranslate();
  const dispatch = useDispatch();
  const quillRef = useRef(null);

  let timeoutId;
  let controller;

  const getMentionModule = () => {
    return {
      allowedChars:
        /^[A-Za-zÅÄÖÜåäöü\d.\w-|&+$]*(?:\s[A-Za-zÅÄÖÜåäöü\d.\w-|&+$]+)*$/i,
      mentionDenotationChars: ['@'],
      minChars: 1,
      maxChars: 180,
      isolateCharacter: true,
      showDenotationChar: true,
      spaceAfterInsert: true,
      mentionContainerClass: 'z-3',
      mentionListClass: 'bg-light-secondary rounded list-unstyled z-3',
      dataAttributes: [
        'id',
        'display_name',
        'denotationChar',
        'link',
        'target',
        'disabled',
        'image_url',
        'iconClass',
        'platform_identifier',
        'platform',
        'platform_css_class',
        'show_denotation_char',
        'is_verified'
      ],
      renderItem: function (item) {
        let iconColorClass = 'text-warning';
        let verifiedText = t('lomavisCreator.Quill.Mention.NotVerified');
        let verificationTooltip = t(
          'lomavisCreator.Quill.Mention.notVerifiedToolTip'
        );
        if (item.is_verified) {
          iconColorClass = 'text-success';
          verifiedText = t('lomavisCreator.Quill.Mention.verified');
          verificationTooltip = t(
            'lomavisCreator.Quill.Mention.verifiedToolTip'
          );
        }
        const element = `<div class="dark d-flex flex-row align-items-center bg-white rounded py-1 px-1 mt-2 mx-2 cursor-pointer"><img src="${item.image_url}" class="w-50px h-50px"><div class="ms-1 d-flex flex-column"><div class="d-flex fw-bolder" style="white-space:pre-wrap;">${item.display_name}</div><div class="w-100"><small data-bs-toggle-old="tooltip" data-bs-placement="top" data-bs-title="${verificationTooltip}"><i class="fas fa-check-circle fa-1x ${iconColorClass} px-2"></i> ${verifiedText}</small></div></div><div class="d-flex flex-grow-1 justify-content-end"><i class="fab ${item.platform_css_class} ms-1 text-primary"></i></div></div>`;
        return element;
      },
      renderLoading() {
        const mentionList = document.querySelector(
          '.mention-container-selector'
        );
        mentionList?.classList?.remove('min-w-300px');
        return `<div class="d-flex"><div class="spinner-border text-primary spinner-border-sm" role="status"></div></div>`;
      },
      source: async (searchTerm, renderList) => {
        // Clear the previous timeout
        if (timeoutId) {
          clearTimeout(timeoutId);
        }

        // Abort any ongoing request
        if (controller) {
          controller.abort();
        }

        // Create new AbortController for this request
        controller = new AbortController();

        timeoutId = setTimeout(async () => {
          try {
            const responses = await Promise.allSettled(
              mentionConfigUrls.map((url) =>
                axios.get(url, {
                  params: { q: searchTerm },
                  signal: controller!.signal
                })
              )
            );

            const mentions = responses.flatMap((res) =>
              res.status === 'fulfilled' ? res.value.data : []
            );

            const mentionList = document.querySelector(
              '.mention-container-selector'
            );
            const quillContainer = document.querySelector('.ql-container');

            mentionList?.classList?.add('min-w-300px');
            mentionList?.setAttribute(
              'style',
              `max-width: ${quillContainer?.clientWidth || 300}px;`
            );

            renderList(mentions, searchTerm);
          } catch (error) {
            console.error('Mention fetch error:', error);
          }
        }, MENTION_TIMEOUT);
      }
    };
  };

  useEffect(() => {
    const quillInstance = new Quill(quillRef.current, {
      theme: 'snow',
      placeholder,
      formats,
      modules: {
        toolbar: [['bold', 'italic']],
        mention: getMentionModule()
      }
    });

    setEditor(quillInstance);
    dispatch(setTextEditorInstance(quillInstance));

    if (quillText) {
      quillInstance.setContents(quillText);
      highlightQuillInput(quillInstance);
      const html = quillInstance.root.innerHTML;
      onChange?.({
        text: quillInstance.getContents(),
        text_as_html: html
      });
    }

    quillInstance.on('text-change', () => {
      const contents = quillInstance.getContents();
      const html = quillInstance.root.innerHTML;
      const plainText = quillInstance.getText();

      onChange?.({
        text: contents,
        text_as_html: html
      });

      const url = getUrlFromText(plainText);
      if (url) {
        getUrlPreview(url).then((preview) => {});
      }
    });

    return () => {
      //@ts-ignore
      quillInstance.off('text-change');
    };
  }, [quillText]);

  useEffect(() => {
    if (!editor) {
      return;
    }

    let markHashtagTimeout;
    editor.on('text-change', function (delta, oldDelta, source) {
      const actionTriggeredByUser =
        source === 'user' || source === QUILL_API_ACTION_ADD_EMOJI;

      if (actionTriggeredByUser) {
        setCursorPositionForQuill(editor);
        if (markHashtagTimeout) {
          clearTimeout(markHashtagTimeout);
          markHashtagTimeout = undefined;
        }

        markHashtagTimeout = setTimeout(function () {
          highlightQuillInput(editor);
          const text = editor.getText();
          const textWithMentions = getTextFromQuill(editor);

          const html = editor.root.innerHTML;

          onChange?.({
            text: editor.getContents(),
            text_as_html: html
          });
          const url = getUrlFromText(text);
          if (url) {
            getUrlPreview(url);
          }
        }, TIMEOUT);
      }
    });

    editor.on('selection-change', function (range, oldRange, source) {
      const actionTriggeredByUser = source === 'copyPaste';
      if (actionTriggeredByUser) {
        setCursorPositionForQuill(editor);
      }
    });

    return () => {
      editor.off('text-change');
      editor.off('selection-change');
    };
  }, [editor]);

  return (
    <div
      className="custom-editor overflow-visible w-100  border rounded ql-container"
      ref={quillRef}
      style={{
        minHeight: 150
      }}
    ></div>
  );
};

export default QuillEditor;
